import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiJson } from "../api/client";
import { tenantPath } from "../utils/tenantContext";

import "./NetworkInspectionPanel.css";
import PcapAnalystViewer from "./PcapAnalystViewer";

const REFRESH_MS = 30000;

function formatTime(value) {
  if (!value) return "Not observed";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function endpoint(ip, port) {
  if (!ip) return "—";

  return port
    ? `${ip}:${port}`
    : ip;
}

function Metric({
  label,
  value,
  detail,
}) {
  return (
    <article className="dpiMetric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function Distribution({
  title,
  rows,
  labelKey,
}) {
  const max = Math.max(
    1,
    ...rows.map(
      (row) => Number(row.count || 0)
    )
  );

  return (
    <section className="dpiCard">
      <div className="dpiCardTitle">
        <h3>{title}</h3>
      </div>

      <div className="dpiDistribution">
        {rows.length === 0 ? (
          <p className="dpiEmpty">
            No observations yet.
          </p>
        ) : null}

        {rows.map((row) => {
          const label =
            row[labelKey] ?? "Unknown";

          const count =
            Number(row.count || 0);

          return (
            <div
              className="dpiDistributionRow"
              key={`${label}-${count}`}
            >
              <div>
                <span>{label}</span>
                <strong>{count}</strong>
              </div>

              <div className="dpiBar">
                <i
                  style={{
                    width:
                      `${Math.max(
                        5,
                        Math.round(
                          count / max * 100
                        )
                      )}%`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function NetworkInspectionPanel() {
  const [data, setData] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [updated, setUpdated] =
    useState(null);

  // WIRELESSPRESS_R18_P4_FINAL_DPI_UI_R1
  const [dpiDetections, setDpiDetections] =
    useState([]);

  const [actionCandidates, setActionCandidates] =
    useState([]);

  const load = useCallback(
    async () => {
      try {
        setError("");

        const [
          result,
          detectionResult,
          actionResult,
        ] = await Promise.all([
          apiJson(
            tenantPath(
              "/network-protection/dpi/dashboard"
            )
          ),

          apiJson(
            tenantPath(
              "/network-protection/dpi/detections?limit=100"
            )
          ),

          apiJson(
            tenantPath(
              "/network-protection/dpi/action-candidates?limit=100"
            )
          ),
        ]);

        setData(result);

        setDpiDetections(
          Array.isArray(detectionResult)
            ? detectionResult
            : []
        );

        setActionCandidates(
          Array.isArray(actionResult)
            ? actionResult
            : []
        );

        setUpdated(new Date());
      } catch (loadError) {
        console.error(
          "Network inspection dashboard:",
          loadError
        );

        setError(
          loadError.message ||
          "Unable to load Network Inspection."
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load();

    const timer =
      window.setInterval(
        load,
        REFRESH_MS
      );

    const tenantHandler = () =>
      load();

    window.addEventListener(
      "wirelesspress-tenant-change",
      tenantHandler
    );

    return () => {
      window.clearInterval(timer);

      window.removeEventListener(
        "wirelesspress-tenant-change",
        tenantHandler
      );
    };
  }, [load]);

  const packetRows =
    data?.recent_packets || [];

  const protocolRows =
    data?.protocol_observations || [];

  const sensors =
    data?.sensors || [];

  const transport =
    data?.transport_distribution || [];

  const direction =
    data?.direction_distribution || [];

  const activeSensorCount =
    useMemo(
      () =>
        sensors.filter(
          (sensor) =>
            sensor.status === "enrolled"
        ).length,
      [sensors]
    );

  const detectionCounts =
    useMemo(() => {
      const result = {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
      };

      dpiDetections.forEach(
        (item) => {
          const severity =
            String(
              item.severity || ""
            ).toLowerCase();

          if (
            Object.prototype.hasOwnProperty.call(
              result,
              severity
            )
          ) {
            result[severity] += 1;
          }
        }
      );

      return result;
    }, [dpiDetections]);

  const openDetectionCount =
    useMemo(
      () =>
        dpiDetections.filter(
          (item) =>
            item.status === "open"
        ).length,
      [dpiDetections]
    );

  if (loading) {
    return (
      <section className="dpiPanel">
        <div className="dpiLoading">
          Loading Network Inspection...
        </div>
      </section>
    );
  }

  return (
    <section className="dpiPanel">
      <div className="dpiHero">
        <div>
          <span className="dpiEyebrow">
            NETWORK INSPECTION
          </span>

          <h2>
            Packet & Protocol Visibility
          </h2>

          <p>
            tcpdump-powered metadata inspection
            with tenant-scoped packet and
            protocol visibility.
          </p>
        </div>

        <div className="dpiHeroStatus">
          <span
            className={
              activeSensorCount > 0
                ? "dpiStatus good"
                : "dpiStatus"
            }
          >
            <i />
            {activeSensorCount > 0
              ? "Sensor enrolled"
              : "No sensor"}
          </span>

          <span>
            Capture:{" "}
            {sensors.some(
              (sensor) =>
                sensor.capture_enabled
            )
              ? "Active"
              : "Off"}
          </span>

          <span>
            Updated:{" "}
            {updated
              ? updated.toLocaleTimeString()
              : "—"}
          </span>
        </div>
      </div>

      {error ? (
        <div className="dpiError">
          {error}
        </div>
      ) : null}

      <div className="dpiMetrics">
        <Metric
          label="Packets observed"
          value={data?.packet_count || 0}
          detail="Metadata records"
        />

        <Metric
          label="Bytes observed"
          value={data?.packet_bytes || 0}
          detail="Parsed packet lengths"
        />

        <Metric
          label="Protocol observations"
          value={
            data?.protocol_observation_count ||
            0
          }
          detail="Derived protocol metadata"
        />

        <Metric
          label="Unique sources"
          value={data?.unique_sources || 0}
          detail="Observed source addresses"
        />

        <Metric
          label="Unique destinations"
          value={
            data?.unique_destinations || 0
          }
          detail="Observed destinations"
        />

        <Metric
          label="Inspection mode"
          value={
            data?.inspection_mode ||
            "metadata_only"
          }
          detail="No payload retention"
        />
      </div>

      <div className="dpiGrid">
        <Distribution
          title="Transport Protocols"
          rows={transport}
          labelKey="protocol"
        />

        <Distribution
          title="Packet Direction"
          rows={direction}
          labelKey="direction"
        />

        <Distribution
          title="Top Source IPs"
          rows={data?.top_sources || []}
          labelKey="address"
        />

        <Distribution
          title="Top Destination IPs"
          rows={
            data?.top_destinations || []
          }
          labelKey="address"
        />

        <Distribution
          title="Top Destination Ports"
          rows={
            data?.top_destination_ports ||
            []
          }
          labelKey="port"
        />

        <section className="dpiCard">
          <div className="dpiCardTitle">
            <h3>Sensor State</h3>
          </div>

          <div className="dpiSensorList">
            {sensors.map(
              (sensor) => (
                <article
                  key={sensor.sensor_uuid}
                  className="dpiSensor"
                >
                  <div>
                    <strong>
                      Sensor {sensor.id}
                    </strong>

                    <code>
                      {sensor.sensor_uuid}
                    </code>
                  </div>

                  <div>
                    <span>
                      {sensor.status}
                    </span>

                    <span>
                      {sensor.capture_interface ||
                        "No interface"}
                    </span>

                    <span>
                      {sensor.capture_level}
                    </span>

                    <span>
                      Capture{" "}
                      {sensor.capture_enabled
                        ? "ON"
                        : "OFF"}
                    </span>
                  </div>
                </article>
              )
            )}
          </div>
        </section>
      </div>

      <section className="dpiCard dpiWide">
        <div className="dpiCardTitle">
          <div>
            <h3>
              Recent Packet Metadata
            </h3>

            <p>
              Latest normalized tcpdump
              observations.
            </p>
          </div>

          <span>
            Last packet:{" "}
            {formatTime(
              data?.last_packet_observed_at
            )}
          </span>
        </div>

        <div className="dpiTableWrap">
          <table className="dpiTable">
            <thead>
              <tr>
                <th>Observed</th>
                <th>Direction</th>
                <th>Protocol</th>
                <th>Source</th>
                <th>Destination</th>
                <th>Bytes</th>
                <th>Flags</th>
              </tr>
            </thead>

            <tbody>
              {packetRows.map(
                (packet) => (
                  <tr
                    key={
                      packet.observation_uuid
                    }
                  >
                    <td>
                      {formatTime(
                        packet.observed_at
                      )}
                    </td>

                    <td>
                      {packet.packet_direction ||
                        "—"}
                    </td>

                    <td>
                      {packet.transport_protocol ||
                        "—"}
                    </td>

                    <td>
                      <code>
                        {endpoint(
                          packet.source_ip,
                          packet.source_port
                        )}
                      </code>
                    </td>

                    <td>
                      <code>
                        {endpoint(
                          packet.destination_ip,
                          packet.destination_port
                        )}
                      </code>
                    </td>

                    <td>
                      {packet.packet_length}
                    </td>

                    <td>
                      {packet.tcp_flags || "—"}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="dpiCard dpiWide">
        <div className="dpiCardTitle">
          <div>
            <h3>
              Protocol Observations
            </h3>

            <p>
              Protocol metadata derived from
              observed network traffic.
            </p>
          </div>
        </div>

        <div className="dpiTableWrap">
          <table className="dpiTable">
            <thead>
              <tr>
                <th>Observed</th>
                <th>Family</th>
                <th>Application</th>
                <th>Source</th>
                <th>Destination</th>
                <th>DNS</th>
                <th>TLS Server</th>
                <th>Classification</th>
              </tr>
            </thead>

            <tbody>
              {protocolRows.map(
                (row) => (
                  <tr
                    key={
                      row.observation_uuid
                    }
                  >
                    <td>
                      {formatTime(
                        row.observed_at
                      )}
                    </td>

                    <td>
                      {row.protocol_family ||
                        "—"}
                    </td>

                    <td>
                      {row.application_protocol ||
                        "—"}
                    </td>

                    <td>
                      <code>
                        {endpoint(
                          row.source_ip,
                          row.source_port
                        )}
                      </code>
                    </td>

                    <td>
                      <code>
                        {endpoint(
                          row.destination_ip,
                          row.destination_port
                        )}
                      </code>
                    </td>

                    <td>
                      {row.dns_query_name ||
                        "—"}
                    </td>

                    <td>
                      {row.tls_server_name ||
                        "—"}
                    </td>

                    <td>
                      {row.classification ||
                        "—"}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="dpiDetectionSection">
        <div className="dpiDetectionHeader">
          <div>
            <span className="dpiEyebrow">
              DETECTION & RESPONSE
            </span>

            <h3>
              DPI Detection & Response Recommendations
            </h3>

            <p>
              Metadata-driven network detections with
              analyst-review recommendations. Automated
              containment remains disabled.
            </p>
          </div>

          <div className="dpiDetectionState">
            <span>
              {openDetectionCount} open
            </span>

            <span>
              {actionCandidates.length} recommendations
            </span>
          </div>
        </div>

        <div className="dpiDetectionMetrics">
          <article>
            <span>Critical</span>
            <strong>{detectionCounts.critical}</strong>
          </article>

          <article>
            <span>High</span>
            <strong>{detectionCounts.high}</strong>
          </article>

          <article>
            <span>Medium</span>
            <strong>{detectionCounts.medium}</strong>
          </article>

          <article>
            <span>Low</span>
            <strong>{detectionCounts.low}</strong>
          </article>

          <article>
            <span>Recommendations</span>
            <strong>{actionCandidates.length}</strong>
          </article>
        </div>

        <section className="dpiCard dpiWide">
          <div className="dpiCardTitle">
            <div>
              <h3>Network Detections</h3>

              <p>
                Rules evaluate packet and protocol metadata
                without payload inspection.
              </p>
            </div>
          </div>

          {dpiDetections.length === 0 ? (
            <div className="dpiCleanState">
              <strong>
                No DPI threat detections
              </strong>

              <span>
                The current captured metadata does not
                meet a configured detection threshold.
              </span>
            </div>
          ) : (
            <div className="dpiTableWrap">
              <table className="dpiTable">
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>Detection</th>
                    <th>Source</th>
                    <th>Destination</th>
                    <th>Protocol</th>
                    <th>Events</th>
                    <th>Last Seen</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {dpiDetections.map(
                    (item) => (
                      <tr
                        key={
                          item.detection_uuid
                        }
                      >
                        <td>
                          <span
                            className={
                              `dpiSeverity dpiSeverity-${String(
                                item.severity ||
                                "low"
                              ).toLowerCase()}`
                            }
                          >
                            {item.severity}
                          </span>
                        </td>

                        <td>
                          <strong>
                            {item.title}
                          </strong>

                          <div className="dpiCellDetail">
                            {item.message}
                          </div>
                        </td>

                        <td>
                          <code>
                            {item.source_ip || "—"}
                          </code>
                        </td>

                        <td>
                          <code>
                            {item.destination_ip || "—"}
                            {item.destination_port
                              ? `:${item.destination_port}`
                              : ""}
                          </code>
                        </td>

                        <td>
                          {item.transport_protocol ||
                            "—"}
                        </td>

                        <td>
                          {item.event_count}
                        </td>

                        <td>
                          {formatTime(
                            item.last_seen_at
                          )}
                        </td>

                        <td>
                          {item.status}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="dpiCard dpiWide">
          <div className="dpiCardTitle">
            <div>
              <h3>
                Response Recommendations
              </h3>

              <p>
                Analyst-review recommendations only.
                No firewall, EDR, or blocking action is
                automatically executed.
              </p>
            </div>
          </div>

          {actionCandidates.length === 0 ? (
            <div className="dpiCleanState">
              <strong>
                No response action required
              </strong>

              <span>
                No current detection generated an action
                recommendation.
              </span>
            </div>
          ) : (
            <div className="dpiRecommendationGrid">
              {actionCandidates.map(
                (action) => (
                  <article
                    key={action.action_uuid}
                    className="dpiRecommendation"
                  >
                    <div>
                      <span>
                        {action.severity}
                      </span>

                      <span>
                        {action.status}
                      </span>
                    </div>

                    <strong>
                      {action.action_type
                        .replaceAll("_", " ")}
                    </strong>

                    <p>
                      {action.recommendation}
                    </p>

                    <small>
                      {action.rationale}
                    </small>
                  </article>
                )
              )}
            </div>
          )}
        </section>
      </section>

            <PcapAnalystViewer />

      <div className="dpiBoundary">
        <strong>
          Inspection boundary
        </strong>

        <span>
          Metadata only · Raw PCAP off ·
          Payload retention off · TLS
          decryption off
        </span>
      </div>
    </section>
  );
}

export default NetworkInspectionPanel;
